import React, { useMemo, useState } from "react";
import { Info, CheckCircle2, Circle, Pencil, X, Check, Clock, Lock, Phone } from "lucide-react";
import { CATEGORY_STYLES } from "@/lib/appUtils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const CATEGORY_KEYS = ["Exercise", "Medication", "Appointment", "Wound Care", "Diet"];

function TaskRow({ task, editable, onToggle, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({
    title: task.title,
    category: task.category,
    time: task.time || "",
    notes: task.notes || "",
  });
  const style = CATEGORY_STYLES[task.category] || CATEGORY_STYLES.Exercise;

  const save = () => {
    onSave && onSave(task.id, draft);
    setEditing(false);
  };
  const cancel = () => {
    setDraft({ title: task.title, category: task.category, time: task.time || "", notes: task.notes || "" });
    setEditing(false);
  };

  if (editing) {
    return (
      <li className="rounded-lg border border-border bg-secondary/40 p-3 space-y-2" data-testid={`task-edit-${task.id}`}>
        <Input
          value={draft.title}
          onChange={(e) => setDraft({ ...draft, title: e.target.value })}
          data-testid={`task-edit-title-${task.id}`}
          className="text-sm h-8"
        />
        <div className="grid grid-cols-2 gap-2">
          <Select value={draft.category} onValueChange={(v) => setDraft({ ...draft, category: v })}>
            <SelectTrigger data-testid={`task-edit-category-${task.id}`} className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CATEGORY_KEYS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input
            type="time"
            value={draft.time}
            onChange={(e) => setDraft({ ...draft, time: e.target.value })}
            data-testid={`task-edit-time-${task.id}`}
            className="h-8 text-xs"
          />
        </div>
        <Textarea
          placeholder="Notes for patient (optional)"
          rows={2}
          value={draft.notes}
          onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
          data-testid={`task-edit-notes-${task.id}`}
          className="text-xs"
        />
        <div className="flex gap-2 justify-end">
          <Button size="sm" variant="ghost" onClick={cancel} data-testid={`task-edit-cancel-${task.id}`}>
            <X className="w-3.5 h-3.5 mr-1" /> Cancel
          </Button>
          <Button size="sm" onClick={save} data-testid={`task-edit-save-${task.id}`}>
            <Check className="w-3.5 h-3.5 mr-1" /> Save
          </Button>
        </div>
      </li>
    );
  }

  return (
    <li className="group flex items-start gap-2" data-testid={`task-item-${task.id}`}>
      {editable ? (
        <button
          onClick={() => onToggle && onToggle(task.id, !task.done)}
          data-testid={`task-toggle-${task.id}`}
          className="mt-0.5 shrink-0 focus:outline-none"
          aria-label={task.done ? "Mark task incomplete" : "Mark task done"}
        >
          {task.done
            ? <CheckCircle2 className="w-4 h-4 brand-text" />
            : <Circle className="w-4 h-4 text-muted-foreground/50 group-hover:text-muted-foreground" />}
        </button>
      ) : (
        <span className="mt-1.5 shrink-0 w-1.5 h-1.5 rounded-full" style={{ background: style.dot }} />
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-start gap-1.5">
          <span className={cn("text-[13px] leading-snug flex-1", task.done ? "line-through text-muted-foreground/60" : "text-foreground")}>
            {task.title}
          </span>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                data-testid={`xai-info-${task.id}`}
                className="mt-0.5 text-muted-foreground/60 hover:text-foreground shrink-0"
                aria-label="Clinical source"
              >
                <Info className="w-3 h-3" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs">
              <div className="text-[10px] uppercase tracking-widest opacity-70 mb-0.5">Explainable AI · source</div>
              <div className="text-sm font-medium">{task.source}</div>
            </TooltipContent>
          </Tooltip>
          {onSave && !task.done && (
            <button
              onClick={() => setEditing(true)}
              data-testid={`task-edit-btn-${task.id}`}
              className="opacity-0 group-hover:opacity-100 text-muted-foreground/60 hover:text-foreground shrink-0 transition-opacity"
              aria-label="Edit task"
            >
              <Pencil className="w-3 h-3" />
            </button>
          )}
        </div>
        <div className="mt-1 flex items-center gap-2 flex-wrap">
          <span className={cn("inline-block text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border font-semibold", style.badge)}>
            {task.category}
          </span>
          {task.time && (
            <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
              <Clock className="w-2.5 h-2.5" /> {task.time}
            </span>
          )}
        </div>
        {task.notes && (
          <div className="mt-1 text-[11px] italic text-muted-foreground line-clamp-2">{task.notes}</div>
        )}
      </div>
    </li>
  );
}

export function RecoveryCalendar({ tasks, currentDay, totalDays = 20, onToggle, onEdit, interactive = false, finalized = false, nurseCallDays = [] }) {
  const days = useMemo(() => {
    const map = {};
    for (let d = 1; d <= totalDays; d++) map[d] = [];
    tasks.forEach((t) => { if (map[t.day]) map[t.day].push(t); });
    return map;
  }, [tasks, totalDays]);
  const nurseSet = useMemo(() => new Set(nurseCallDays || []), [nurseCallDays]);

  return (
    <TooltipProvider delayDuration={100}>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="recovery-calendar">
        {Array.from({ length: totalDays }, (_, i) => i + 1).map((day) => {
          const dayTasks = days[day];
          const isToday = day === currentDay;
          const isPast = day < currentDay;
          const doneCount = dayTasks.filter((t) => t.done).length;
          const pct = dayTasks.length ? Math.round((doneCount / dayTasks.length) * 100) : 0;
          const hasNurseCall = nurseSet.has(day);
          return (
            <div
              key={day}
              data-testid={`day-card-${day}`}
              className={cn(
                "surface-card p-4 transition-all",
                isToday ? "border-foreground/40 shadow-sm" : "",
                hasNurseCall && !isToday ? "border-amber-300/70 dark:border-amber-800" : "",
                isPast && "opacity-90"
              )}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className={cn(
                    "w-7 h-7 rounded-lg flex items-center justify-center text-[11px] font-semibold border",
                    isToday
                      ? "bg-foreground text-background border-foreground"
                      : isPast
                      ? "bg-secondary text-muted-foreground border-border"
                      : "bg-card text-foreground border-border"
                  )}>
                    {day}
                  </div>
                  <div className="text-[11px] font-medium">
                    <div className="text-foreground">Day {day}</div>
                    {isToday && <div className="text-[9px] uppercase tracking-wider brand-text font-semibold">Today</div>}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {hasNurseCall && (
                    <span
                      data-testid={`nurse-call-badge-${day}`}
                      className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-semibold px-1.5 py-0.5 rounded-full border border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
                      title="Nurse call scheduled"
                    >
                      <Phone className="w-2.5 h-2.5" /> Nurse
                    </span>
                  )}
                  {finalized && <Lock className="w-3 h-3 text-muted-foreground" aria-label="Finalized" />}
                  <div className="text-[11px] text-muted-foreground font-medium">{pct}%</div>
                </div>
              </div>

              <ul className="space-y-2">
                {dayTasks.map((t) => (
                  <TaskRow
                    key={t.id}
                    task={t}
                    editable={interactive}
                    onToggle={onToggle}
                    onSave={onEdit && !finalized ? onEdit : null}
                  />
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </TooltipProvider>
  );
}
